import NoImage from 'assets/images/noimage.png'
import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useDebounce } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ChevronDown, RefreshCw } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, CardBody, Spinner } from 'reactstrap'
import { URL_WORKPLACE } from 'utils/pathUrl'
import {
  useFetchFindoraItemsQuery,
  useSyncFindoraEmployeesMutation,
  useSyncFindoraItemsMutation,
} from './syncDataListApiSlice'
import { setFetchOption } from './syncDataListSlice'

const SyncDataList = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const { tag } = useSelector((state) => state.syncDataList)

  const isMobile = useIsMobile()

  const [searchInput, setSearchInput] = useState('')
  const [isFilter, setIsFilter] = useState(false)
  const [isSync, setIsSync] = useState(false)
  const [imageItems, setImagesItems] = useState([])
  const [modalOpenImage, setModalOpenImage] = useState(false)

  const [syncFindoraItems, { isLoading: isLoadingSyncFindoraItems }] =
    useSyncFindoraItemsMutation()

  const [syncFindoraEmployees, { isLoading: isLoadingSyncFindoraEmployees }] =
    useSyncFindoraEmployeesMutation()

  const { data, isLoading, refetch, isFetching } = useFetchFindoraItemsQuery(
    {
      page: tag?.page ?? 0,
      size: tag?.size ?? 10,
      s: tag?.s,
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
    },
    { refetchOnMountOrArgChange: true, skip: false },
  )

  const onHandleOpenImageModal = (imageUrls) => {
    setImagesItems([imageUrls])
    setModalOpenImage(true)
  }

  const columns = [
    {
      name: <span>Nama Barang</span>,
      selector: 'nameItem',
      grow: 2,
      sortable: true,
      sortField: 'nameItem',
      cell: (row, index) => {
        return (
          <div
            className="flex-centered gap-2"
            data-testid={`sync-data-table-row-${row?.id}`}
          >
            <img
              src={
                row?.linkFoto && row?.linkFoto.length > 0
                  ? row?.linkFoto
                  : NoImage
              }
              alt={row?.nameItem || `barang ${index + 1}`}
              width={32}
              height={32}
              className="rounded-circle"
              onClick={() => onHandleOpenImageModal(row?.linkFoto || NoImage)}
              style={{ cursor: 'pointer' }}
            />
            <span className="fw-bold text-primary m-0">{row?.nameItem}</span>
          </div>
        )
      },
    },
    {
      name: <span>SKU</span>,
      selector: 'SKU',
      grow: 2,
      cell: (row) => {
        return <span>{row?.SKU ?? '-'}</span>
      },
    },
  ]

  const handleSyncData = async () => {
    try {
      setIsSync(true)
      const syncFindoraItemsResponse = await syncFindoraItems()
      const syncFindoraEmployeesResponse = await syncFindoraEmployees()
      if (
        !syncFindoraItemsResponse?.error?.data?.message &&
        !syncFindoraEmployeesResponse?.error?.data?.message
      ) {
        enqueueSnackbar('Data berhasil disinkronisasi', {
          variant: 'success',
        })
        dispatch(setFetchOption({ page: 0, s: undefined }))
        setSearchInput('')
        refetch()
      } else {
        enqueueSnackbar('Gagal sinkronisasi data', {
          variant: 'error',
        })
      }
    } catch (error) {
      enqueueSnackbar('Gagal sinkronisasi data', {
        variant: 'error',
      })
    } finally {
      setIsSync(false)
    }
  }

  const handleSort = (column, direction) => {
    dispatch(
      setFetchOption({
        sortby: column.sortField,
        sortdir: direction?.toUpperCase(),
      }),
    )
  }

  useEffect(() => {
    dispatch(setFetchOption({ page: 0 }))
  }, [searchInput])

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFetchOption({
        page: 0,
        s: value,
      }),
    )
  }, 600)

  const onHandleSearchChange = (value) => {
    if (!value?.trim()) {
      setIsFilter(false)
    } else {
      setIsFilter(true)
    }
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && tag?.s.length === 0) {
      e.preventDefault()
    }
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* MODAL IMAGE PREVIEW */}
      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={imageItems}
      />

      <div className="mt-3">
        <BreadCrumbs
          breadCrumbParent={'Alat Bantu'}
          breadCrumbParentLink={URL_WORKPLACE}
          breadCrumbActive="Daftar Sinkronisasi Barang"
        />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <DataTable
            responsive
            persistTableHead
            defaultSortFieldId={1}
            defaultSortAsc={true}
            columns={columns}
            data={data?.items || []}
            progressPending={isFetching}
            paginationTotalRows={data?.totalItems}
            pagination
            paginationServer
            paginationComponent={() => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={tag?.page}
                rowsPerPage={tag?.size}
                setCurrentPage={(e) => {
                  dispatch(setFetchOption({ page: e }))
                }}
                setRowsPerPage={(e) => {
                  dispatch(setFetchOption({ size: e }))
                }}
              />
            )}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner />
              </div>
            }
            onSort={handleSort}
            sortIcon={<ChevronDown />}
            sortServer
            subHeader={true}
            subHeaderComponent={
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h5 className="fw-semibold">Daftar Sinkronisasi Barang</h5>

                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      color="primary"
                      className="d-flex align-items-center gap-2"
                      onClick={handleSyncData}
                      disabled={isSync}
                      data-testid="sync-data-button"
                    >
                      {isSync ? <Spinner size="sm" /> : <RefreshCw size={16} />}
                      Sinkronisasi
                    </Button>
                  </div>
                </div>

                <div className="my-4">
                  <PMBSearchInputGroup
                    label="Cari"
                    value={searchInput}
                    placeholder="Cari barang..."
                    onChange={(e) => onHandleSearchChange(e.target.value)}
                    maxWidth={isMobile ? '100%' : '15%'}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            }
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isFilter
                  ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                  : 'Belum ada daftar barang yang tersedia'}
              </p>
            }
          />
        </CardBody>
      </Card>
    </div>
  )
}

export default SyncDataList
